import { liveQuery } from 'dexie';
import { useSyncExternalStore } from 'react';

/**
 * One live copy of a table query for the whole app. The first screen that needs it starts the
 * query; later screens read the copy straight away (no empty first render, no re-reading the table
 * on every page visit), and every change to the table updates them all.
 */
export function sharedQuery<T>(query: () => Promise<T>) {
  let value: T | undefined;
  let started = false;
  const listeners = new Set<() => void>();
  const start = () => {
    if (started) return;
    started = true;
    liveQuery(query).subscribe({ next: (v) => { value = v; listeners.forEach((l) => l()); }, error: () => {} });
  };
  const subscribe = (l: () => void) => { start(); listeners.add(l); return () => { listeners.delete(l); }; };
  const get = () => value;
  return { use: () => useSyncExternalStore(subscribe, get), get, start };
}

/** Derive once per new value (e.g. a Map or search index), shared by every reader. */
export function memoLast<A extends object, R>(fn: (a: A) => R) {
  let lastIn: A | undefined;
  let lastOut: R;
  return (a: A): R => {
    if (a !== lastIn) { lastIn = a; lastOut = fn(a); }
    return lastOut;
  };
}
